import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ShieldCheck, UserCog, UserMinus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, Field, TextInput } from "./kit";

const ROLE_META = {
  admin: { label: "Admin", icon: ShieldCheck, desc: "Full access to everything" },
  staff: { label: "Staff", icon: UserCog, desc: "Support inbox, leads, VA profiles, bookings calendar" },
  limited: { label: "Limited", icon: UserMinus, desc: "Lead directory and email compose only" },
};

export default function TeamAccounts({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.name} can now sign in at /admin`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} removed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-management">
      <Card title="Team Management" desc="Create staff logins with role-based access. Each role only sees the sections it is allowed to see.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></Field>
          <Field label="Email"><TextInput value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="work@email.com" /></Field>
          <Field label="Password (min 8 characters)"><TextInput type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <Field label="Role">
            <select className="admin-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role-select">
              {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.desc}</option>)}
            </select>
          </Field>
        </div>
        <button className="btn btn-sm btn-primary mt-5" onClick={create} data-testid="staff-create-button">Create Staff Login</button>
      </Card>

      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display font-bold text-white mb-4">Accounts ({users.length})</h3>
        <div className="space-y-3">
          {users.map((u) => {
            const Meta = ROLE_META[u.role]?.icon || ShieldCheck;
            return (
              <div key={u.id} className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-4" data-testid={`staff-row-${u.id}`}>
                <span className="w-10 h-10 rounded-full bg-[#00BFFF]/12 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                  <Meta size={17} className="text-cyan-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm">{u.name} {u.id === currentUser?.id && <span className="text-[10px] text-cyan-300 font-semibold">(you)</span>}</div>
                  <div className="text-xs text-slate-400">{u.email}</div>
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border flex-shrink-0 ${u.role === "admin" ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" : u.role === "staff" ? "bg-blue-400/15 text-blue-300 border-blue-400/30" : "bg-slate-400/15 text-slate-300 border-slate-400/30"}`}>
                  {ROLE_META[u.role]?.label || u.role}
                </span>
                {u.id !== currentUser?.id && (
                  <button onClick={() => remove(u)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete account" data-testid={`staff-delete-${u.id}`}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
